/**
 * ASCE 7 Hazard API types for Wind and Snow load retrieval.
 * API Documentation: https://api-hazard.asce.org/docs
 */

export interface AsceWindRequest {
  lat: number;
  lon: number;
  standardsVersion: string;
  riskLevel: number;
}

export interface AsceSnowRequest {
  lat: number;
  lon: number;
  standardsVersion: string;
  riskLevel: number;
}

export interface AsceSiteInfo {
  latitude: number | string;
  longitude: number | string;
  elevation: string;
}

export interface AsceWindResponse {
  requestInfo: {
    siteInfo: AsceSiteInfo;
  };
  wind: {
    mriWindResults: Record<string, string>;
    isHurricaneWindDebrisZone: boolean;
    isSpecialWindZone: boolean;
    isNewHampshire_7_16?: boolean;
  };
}

export interface AsceSnowFeatureAttributes {
  Load1_1: number;
  Display_1: string;
  Elevation1_1: number;
  lb_ft2: number;
  kN_m2: number;
}

/**
 * 7-22 flat key-value entry returned by the ASCE snow endpoint.
 * Keys include "riskCategory1", "riskCategory2", "mri20yr", "elevation", "winterWind", etc.
 */
export interface AsceSnowKeyValue {
  key: string;
  value: string | number | null;
}

export interface AsceSnowResponse {
  requestInfo: {
    siteInfo: AsceSiteInfo;
  };
  snow: {
    noSnowFeatures: boolean;
    state: string;
    /**
     * Format varies by standards version:
     *  - 7-10 / 7-16: array of { features: [{ attributes: AsceSnowFeatureAttributes }] }
     *  - 7-22: array of { key, value } OR object with riskCategoryXX / mri20yr properties
     */
    snowResults:
      | Array<{
          features?: Array<{
            attributes: AsceSnowFeatureAttributes;
          }>;
          key?: string;
          value?: string | number | null;
        }>
      | (Record<string, string | number | null> & {
          attributes?: Record<string, string | number | null>;
        });
  };
}

export interface AsceLoadResult {
  windLoadMph: number;
  snowLoadPsf: number;
  snowParseWarning?: boolean;
  elevation: string;
  isHurricaneWindDebrisZone: boolean;
  isSpecialWindZone: boolean;
  isNewHampshire_7_16: boolean;
  mriWindResults: Record<string, string>;
  riskLevel: number;
  rawWindResponse?: AsceWindResponse;
  rawSnowResponse?: AsceSnowResponse;
}

/** MRI key lookup based on Risk Category per ASCE 7-10 */
export const MRI_KEY_BY_RISK_LEVEL: Record<number, string> = {
  1: '300', // Risk Category I (non-permanent dwelling)
  2: '700', // Risk Category II (permanent dwelling)
  3: '1700', // Risk Category III (future)
  4: '3000', // Risk Category IV (future)
};

export const ASCE_STANDARDS_VERSION = '7-10';

export const WIND_LOAD_OVERRIDE_OPTIONS = [
  85, 90, 95, 100, 105, 110, 115, 120, 130, 140, 150, 160, 170, 180, 190, 200,
];

export const SNOW_LOAD_OVERRIDE_OPTIONS = [
  0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 70, 80, 90, 100,
];
